import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { adminApi } from "../../../lib/admin";
import type {
  AdminPaymentActionRequest,
  AdminPaymentAssignmentAllocationResponse,
  AdminPaymentDetailResponse,
  AdminPaymentListItemResponse,
} from "../../../types/adminTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatUsdFromCents } from "../../../utils/CurrencyUtils";
import { formatDateTime } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";

type AdminPaymentDetailModalProps = {
  payment: AdminPaymentListItemResponse | null;
  isOpen: boolean;
  canWrite: boolean;
  onClose: () => void;
};

const requiresManualReviewRefund = (detail: AdminPaymentDetailResponse) =>
  detail.paymentType === "GIG"
  && detail.status === "FAILED"
  && detail.fundedAt !== null
  && detail.refundedAt === null;

const openIssueStatuses = new Set(["OPEN", "UNDER_REVIEW", "AWAITING_SETTLEMENT_APPROVAL"]);
const actionablePaymentStatuses = new Set(["FUNDED", "RELEASE_READY", "PARTIALLY_REFUNDED", "PARTIALLY_SETTLED"]);

type AssignmentActionDraft = {
  action: AdminPaymentActionRequest["action"];
  gigRequestId: string;
  teenName: string;
};

const canTakeManualAssignmentAction = (
  detail: AdminPaymentDetailResponse,
  allocation: AdminPaymentAssignmentAllocationResponse,
) =>
  detail.paymentType === "GIG"
  && allocation.gigRequestId !== null
  && allocation.assignmentStatus === "ASSIGNED"
  && actionablePaymentStatuses.has(detail.status)
  && !openIssueStatuses.has(allocation.latestIssueStatus ?? "");

const assignmentActionLabel = (action: AdminPaymentActionRequest["action"]) =>
  action === "RELEASE_PAYOUT" ? "Release payout" : "Refund adult";

const AdminPaymentDetailModal = ({ payment, isOpen, canWrite, onClose }: AdminPaymentDetailModalProps) => {
  const queryClient = useQueryClient();
  const [refundReason, setRefundReason] = useState("");
  const [assignmentAction, setAssignmentAction] = useState<AssignmentActionDraft | null>(null);
  const [assignmentActionReason, setAssignmentActionReason] = useState("");
  const [paymentActionFeedback, setPaymentActionFeedback] = useState<string | null>(null);

  const paymentDetailQuery = useQuery({
    queryKey: ["admin-payment-detail", payment?.id],
    queryFn: () => adminApi.getPaymentDetail(payment!.id, payment!.paymentType),
    enabled: isOpen && payment !== null,
  });

  const detail = paymentDetailQuery.data;
  const canRefundInvalidatedCheckout = detail ? requiresManualReviewRefund(detail) : false;
  const assignmentAllocations = detail?.assignmentAllocations ?? [];
  const hasMultipleAssignmentAllocations = detail?.paymentType === "GIG" && assignmentAllocations.length > 1;

  useEffect(() => {
    if (!isOpen) {
      setRefundReason("");
      setAssignmentAction(null);
      setAssignmentActionReason("");
      setPaymentActionFeedback(null);
    }
  }, [isOpen]);

  const refundMutation = useMutation({
    mutationFn: () => adminApi.performPaymentAction(detail!.id, {
      action: "REFUND_ADULT",
      reason: refundReason.trim(),
    }),
    onSuccess: async () => {
      setPaymentActionFeedback("Invalidated checkout payment refunded.");
      setRefundReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-payments"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-payment-detail", payment?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setPaymentActionFeedback(apiErrorToMessage(error, "Unable to refund this payment."));
    },
  });

  const assignmentActionMutation = useMutation({
    mutationFn: () => adminApi.performPaymentAction(detail!.id, {
      action: assignmentAction!.action,
      reason: assignmentActionReason.trim(),
      gigRequestId: assignmentAction!.gigRequestId,
    }),
    onSuccess: async () => {
      setPaymentActionFeedback(`${assignmentAction ? assignmentActionLabel(assignmentAction.action) : "Assignment action"} completed.`);
      setAssignmentAction(null);
      setAssignmentActionReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-payments"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-payment-detail", payment?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setPaymentActionFeedback(apiErrorToMessage(error, "Unable to update this assignment."));
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={payment?.gigTitle ?? "Payment detail"} panelClassName="max-w-5xl">
      {paymentDetailQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading payment detail...</p> : null}
      {paymentDetailQuery.error ? <p className="text-sm text-red-600">{apiErrorToMessage(paymentDetailQuery.error, "Failed to load payment detail.")}</p> : null}

      {detail ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
              <div className="mt-3"><AdminPillBadge>{detail.status}</AdminPillBadge></div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                {detail.paymentType === "GIG" ? (hasMultipleAssignmentAllocations ? "Teen payout pool" : "Teen pay") : "Listing fee"}
              </p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatUsdFromCents(detail.amountCents)}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                {detail.paymentType === "GIG" ? "Platform fee" : "Collected amount"}
              </p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatUsdFromCents(detail.platformFeeCents)}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Total charged</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{formatUsdFromCents(detail.totalChargeCents)}</p>
            </div>
          </div>

          {paymentActionFeedback ? (
            <p className="rounded-lg border border-myteengig-border bg-myteengig-background px-4 py-3 text-sm text-myteengig-text-primary">
              {paymentActionFeedback}
            </p>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">{detail.paymentType === "GIG" ? "Participants" : "Business"}</p>
              <div className="mt-3 space-y-3 text-sm text-myteengig-text-muted">
                {detail.paymentType === "GIG" ? (
                  hasMultipleAssignmentAllocations ? (
                    <div>
                      <p className="font-medium text-myteengig-text-primary">Crew</p>
                      <p>{assignmentAllocations.length} assignment allocations</p>
                      <p>See per-teen allocation rows below.</p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium text-myteengig-text-primary">Teen</p>
                      <p>{detail.teenName}</p>
                      <p>{detail.teenEmail ?? "No email available"}</p>
                    </div>
                  )
                ) : null}
                <div>
                  <p className="font-medium text-myteengig-text-primary">{detail.paymentType === "GIG" ? "Adult" : "Business account"}</p>
                  <p>{detail.adultName}</p>
                  <p>{detail.adultEmail ?? "No email available"}</p>
                </div>
                {detail.paymentType === "GIG" && detail.guardianUserId ? (
                  <div>
                    <p className="font-medium text-myteengig-text-primary">Guardian payout owner</p>
                    <p>{detail.guardianName}</p>
                    <p>{detail.guardianEmail ?? "No email available"}</p>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Lifecycle</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>Created: {formatDateTime(detail.createdAt)}</p>
                <p>Agreement accepted: {detail.agreementAcceptedAt ? formatDateTime(detail.agreementAcceptedAt) : "Not applicable"}</p>
                <p>{detail.paymentType === "GIG" ? "Funded" : "Paid"}: {detail.fundedAt ? formatDateTime(detail.fundedAt) : "Not paid"}</p>
                <p>Released: {detail.releasedAt ? formatDateTime(detail.releasedAt) : "Not released"}</p>
                <p>Refunded: {detail.refundedAt ? formatDateTime(detail.refundedAt) : "Not refunded"}</p>
                <p>Transfer reversed: {detail.transferReversedAt ? formatDateTime(detail.transferReversedAt) : "Not reversed"}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Stripe references</p>
              <div className="mt-3 space-y-2 break-all text-sm text-myteengig-text-muted">
                <p>Customer: {detail.stripeCustomerId ?? "Not available"}</p>
                <p>Checkout session: {detail.stripeCheckoutSessionId ?? "Not available"}</p>
                <p>Payment intent: {detail.stripePaymentIntentId ?? "Not available"}</p>
                <p>Charge: {detail.stripeChargeId ?? "Not available"}</p>
                <p>Transfer: {detail.stripeTransferId ?? "Not available"}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Linked issue</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>Status: {detail.latestIssueStatus ?? "No issue"}</p>
                <p>Issue ID: {detail.latestIssueId ?? "No issue"}</p>
                <p>{detail.latestIssueMessage ?? "No issue message recorded."}</p>
              </div>
            </div>
          </div>

          {detail.paymentType === "GIG" ? (
            <div className="rounded-2xl border border-myteengig-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-myteengig-text-primary">Assignment allocations</p>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    Parent checkout totals stay above; each row shows the amount and outcome for one teen assignment.
                  </p>
                </div>
                <AdminPillBadge>{assignmentAllocations.length} rows</AdminPillBadge>
              </div>
              {assignmentAllocations.length === 0 ? (
                <p className="mt-4 text-sm text-myteengig-text-muted">No assignment allocation snapshots recorded yet.</p>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-myteengig-border text-left text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide text-myteengig-text-muted">
                        <th className="px-3 py-2 font-semibold">Teen</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                        <th className="px-3 py-2 font-semibold">Seats</th>
                        <th className="px-3 py-2 font-semibold">Poster charge</th>
                        <th className="px-3 py-2 font-semibold">Teen payout</th>
                        <th className="px-3 py-2 font-semibold">Fees</th>
                        <th className="px-3 py-2 font-semibold">Outcome</th>
                        <th className="px-3 py-2 font-semibold">Issue</th>
                        <th className="px-3 py-2 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-myteengig-border">
                      {assignmentAllocations.map((allocation) => {
                        const canTakeAction = canWrite && canTakeManualAssignmentAction(detail, allocation);
                        return (
                          <tr key={allocation.assignmentId} className="align-top">
                            <td className="px-3 py-3">
                              <p className="font-medium text-myteengig-text-primary">{allocation.teenName}</p>
                              <p className="mt-1 text-xs text-myteengig-text-muted">{allocation.teenEmail ?? "No email available"}</p>
                              <p className="mt-1 break-all text-xs text-myteengig-text-muted">Request: {allocation.gigRequestId ?? "Unavailable"}</p>
                            </td>
                            <td className="px-3 py-3">
                              <div className="space-y-2">
                                <AdminPillBadge>{allocation.assignmentStatus}</AdminPillBadge>
                                <p className="text-xs text-myteengig-text-muted">{allocation.paymentStatus}</p>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-myteengig-text-primary">{allocation.coveredSeats}</td>
                            <td className="px-3 py-3 text-myteengig-text-primary">{formatUsdFromCents(allocation.allocatedPosterChargeCents)}</td>
                            <td className="px-3 py-3 text-myteengig-text-primary">{formatUsdFromCents(allocation.allocatedTeenPayoutCents)}</td>
                            <td className="px-3 py-3 text-myteengig-text-muted">
                              <p>Platform: {formatUsdFromCents(allocation.allocatedPlatformFeeCents)}</p>
                              <p>Processing: {formatUsdFromCents(allocation.allocatedProcessingFeeCents)}</p>
                            </td>
                            <td className="px-3 py-3 text-myteengig-text-muted">
                              <p>Refunded: {formatUsdFromCents(allocation.refundedAmountCents)}</p>
                              <p>Released: {formatUsdFromCents(allocation.releasedAmountCents)}</p>
                              {allocation.removedAt ? <p>Removed: {formatDateTime(allocation.removedAt)}</p> : null}
                            </td>
                            <td className="px-3 py-3 text-myteengig-text-muted">
                              <p>{allocation.latestIssueStatus ?? "No issue"}</p>
                              {allocation.latestIssueId ? <p className="mt-1 break-all text-xs">Issue: {allocation.latestIssueId}</p> : null}
                              {allocation.latestIssueMessage ? <p className="mt-1 text-xs">{allocation.latestIssueMessage}</p> : null}
                            </td>
                            <td className="px-3 py-3">
                              {canTakeAction ? (
                                <div className="flex flex-col gap-2">
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={() => {
                                      setPaymentActionFeedback(null);
                                      setAssignmentAction({
                                        action: "RELEASE_PAYOUT",
                                        gigRequestId: allocation.gigRequestId!,
                                        teenName: allocation.teenName,
                                      });
                                    }}
                                  >
                                    Release {allocation.teenName}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={() => {
                                      setPaymentActionFeedback(null);
                                      setAssignmentAction({
                                        action: "REFUND_ADULT",
                                        gigRequestId: allocation.gigRequestId!,
                                        teenName: allocation.teenName,
                                      });
                                    }}
                                  >
                                    Refund {allocation.teenName}
                                  </Button>
                                </div>
                              ) : (
                                <p className="text-xs text-myteengig-text-muted">No direct action</p>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {assignmentAction ? (
            <div className="rounded-2xl border border-myteengig-border bg-myteengig-background p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-myteengig-text-primary">{assignmentActionLabel(assignmentAction.action)}</p>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    This manual action affects {assignmentAction.teenName} only. Sibling assignments on this parent checkout are not changed.
                  </p>
                </div>
                <AdminPillBadge>{assignmentAction.gigRequestId}</AdminPillBadge>
              </div>
              <label htmlFor="admin-payment-assignment-action-reason" className="mt-4 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                Assignment action reason
              </label>
              <textarea
                id="admin-payment-assignment-action-reason"
                value={assignmentActionReason}
                onChange={(event) => setAssignmentActionReason(event.target.value)}
                rows={3}
                className="mt-2 w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                placeholder={`Reason for ${assignmentActionLabel(assignmentAction.action).toLowerCase()} on ${assignmentAction.teenName}`}
              />
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={assignmentActionMutation.isPending}
                  onClick={() => {
                    setAssignmentAction(null);
                    setAssignmentActionReason("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={assignmentActionReason.trim().length === 0 || assignmentActionMutation.isPending}
                  onClick={() => assignmentActionMutation.mutate()}
                >
                  {assignmentActionMutation.isPending ? "Submitting..." : assignmentActionLabel(assignmentAction.action)}
                </Button>
              </div>
            </div>
          ) : null}

          {canRefundInvalidatedCheckout ? (
            <div className="rounded-2xl border border-dashed border-red-300 bg-red-50/60 p-4">
              <p className="text-sm font-semibold text-red-900">Manual review refund</p>
              <p className="mt-2 text-sm text-red-800">
                Stripe completed payment after this checkout was marked failed. The selected crew was not confirmed, so refund the full charge after support review.
              </p>
              <label htmlFor="admin-payment-refund-reason" className="mt-4 block text-xs font-semibold uppercase tracking-wide text-red-900">
                Reason
              </label>
              <textarea
                id="admin-payment-refund-reason"
                value={refundReason}
                onChange={(event) => setRefundReason(event.target.value)}
                rows={3}
                className="mt-2 w-full rounded-lg border border-red-200 bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                placeholder="Checkout was invalidated before Stripe reported success. Refunding the adult in full."
              />
              <div className="mt-4 flex justify-end">
                <Button
                  disabled={!canWrite || refundReason.trim().length === 0 || refundMutation.isPending}
                  onClick={() => refundMutation.mutate()}
                >
                  {refundMutation.isPending ? "Refunding..." : "Refund adult"}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Connected account payout outcomes</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              These events come from Stripe’s connected-account payout lifecycle. They reflect bank-payout outcomes for the payout account, which can happen after the platform transfer is released.
            </p>
            {detail.connectedAccountPayoutEvents.length === 0 ? (
              <p className="mt-3 text-sm text-myteengig-text-muted">No connected-account payout events recorded yet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {detail.connectedAccountPayoutEvents.map((event) => (
                  <div key={event.id} className="rounded-2xl bg-myteengig-background p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <AdminPillBadge>{event.status}</AdminPillBadge>
                        <p className="text-sm font-semibold text-myteengig-text-primary">{formatUsdFromCents(event.amountCents)}</p>
                      </div>
                      <p className="text-xs text-myteengig-text-muted">
                        {event.stripeCreatedAt ? formatDateTime(event.stripeCreatedAt) : "Stripe timestamp unavailable"}
                      </p>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-myteengig-text-muted">
                      <p>Payout ID: {event.stripePayoutId}</p>
                      <p>Method: {event.method ?? "Unknown"}{event.automatic ? " • automatic" : ""}</p>
                      <p>Arrival date: {event.arrivalDate ? formatDateTime(event.arrivalDate) : "Unknown"}</p>
                      <p>Last event: {event.lastEventType ?? "Unknown"}</p>
                      {event.failureCode || event.failureMessage ? (
                        <p className="text-rose-700">
                          Failure: {[event.failureCode, event.failureMessage].filter(Boolean).join(" - ")}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default AdminPaymentDetailModal;
